import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { DecorativeIcon } from './DecorativeIcon';
import { CollabSearchInput } from '@nimbalyst/collab-bundle/docs-ui';
import {
  inviteMember,
  listMembers,
  removeMember,
  resendInvitation,
  revokeInvitation,
  updateMemberRole,
  type MembersResponse,
  type TeamMember,
  type TeamSummary,
} from '../api';
import type { TeamJwt } from '../auth/teamJwt';
import { PanelState } from '../components/Chrome';
import {
  BUTTON_GHOST,
  BUTTON_GHOST_DANGER,
  CARD,
  FIELD,
  MENU_ITEM,
  MENU_SURFACE,
  PILL,
  SECTION_SUBTITLE,
  SECTION_TITLE,
} from '../controlStyles';
import { InviteLinksPanel } from './InviteLinksPanel';
import { InviteMembersPopover } from './InviteMembersPopover';
import { ProjectAccessPanel } from './ProjectAccessPanel';
import {
  DEFAULT_MEMBER_SORT,
  MEMBER_SORT_LABELS,
  ORG_ROLES,
  assignableRoles,
  canManageMembers,
  describeInviteFailure,
  describeInviteFailures,
  memberIdentity,
  partitionMembership,
  searchMembers,
  sortMembers,
  summarizeInviteBatch,
  type InviteBatchResult,
  type MemberSort,
  type MemberSortKey,
  type OrgRole,
} from './memberDirectory';

type TeamJwtProvider = () => Promise<TeamJwt>;

function formatDate(value: string): string {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 'Unknown' : new Date(parsed).toLocaleDateString();
}

function MemberAvatar({ initials, muted = false }: { initials: string; muted?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={'member-avatar grid h-8 w-8 shrink-0 place-items-center rounded-full text-[0.7rem] font-semibold '
        + (muted
          ? 'border border-dashed border-nim text-nim-muted bg-nim-secondary'
          : 'text-[var(--nim-on-primary)] bg-[linear-gradient(135deg,var(--nim-primary),var(--nim-primary-hover))]')}
    >
      {initials}
    </span>
  );
}

function MemberSortMenu({ sort, onChange }: { sort: MemberSort; onChange: (next: MemberSort) => void }) {
  const [open, setOpen] = useState(false);
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const interactions = useInteractions([
    useClick(floating.context),
    useDismiss(floating.context),
    useRole(floating.context, { role: 'menu' }),
  ]);
  const pick = (key: MemberSortKey) => {
    // Re-picking the active key flips direction, which is the one gesture a
    // sort control has to have and the only way to reach descending.
    onChange(key === sort.key
      ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' });
    setOpen(false);
  };
  return (
    <>
      <button
        ref={floating.refs.setReference}
        {...interactions.getReferenceProps()}
        className={`member-sort-button ${BUTTON_GHOST} h-[2.1rem] gap-1 border border-nim px-2.5`}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <DecorativeIcon icon="swap_vert" size={16} />
        <span className="text-[0.78rem]">{MEMBER_SORT_LABELS[sort.key]}</span>
        <DecorativeIcon icon={sort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'} size={13} />
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={floating.context} modal={false} initialFocus={-1}>
            <div
              ref={floating.refs.setFloating}
              style={floating.floatingStyles}
              {...interactions.getFloatingProps()}
              className={`member-sort-menu ${MENU_SURFACE}`}
            >
              {(Object.keys(MEMBER_SORT_LABELS) as MemberSortKey[]).map((key) => (
                <button
                  key={key}
                  className={MENU_ITEM}
                  type="button"
                  role="menuitemradio"
                  aria-checked={sort.key === key}
                  onClick={() => pick(key)}
                >
                  <DecorativeIcon
                    icon={sort.key === key ? (sort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'remove'}
                    size={14}
                    className={sort.key === key ? '' : 'opacity-0'}
                  />
                  <span>{MEMBER_SORT_LABELS[key]}</span>
                </button>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

function RoleControl({
  member,
  callerRole,
  disabled,
  onChange,
}: {
  member: TeamMember;
  callerRole: string;
  disabled: boolean;
  onChange: (role: OrgRole) => void;
}) {
  const roles = assignableRoles(callerRole);
  if (roles.length === 0) {
    return (
      <span className={`${PILL} member-role-pill text-nim-muted bg-nim-tertiary`}>{member.role}</span>
    );
  }
  return (
    <select
      className={`member-role-select ${FIELD} w-[8rem] capitalize`}
      aria-label={`Role for ${memberIdentity(member).title}`}
      value={member.role}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as OrgRole)}
    >
      {ORG_ROLES.map((role) => (
        // A role above the caller's own is shown but not selectable, so an
        // admin can still read that someone is an owner.
        <option key={role} value={role} disabled={!roles.includes(role)}>{role}</option>
      ))}
    </select>
  );
}

export function OrganizationMembers({ getTeamJwt, team }: { getTeamJwt: TeamJwtProvider; team: TeamSummary }) {
  const [data, setData] = useState<MembersResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<MemberSort>(DEFAULT_MEMBER_SORT);

  const load = useCallback(async (signal?: AbortSignal) => {
    const jwt = await getTeamJwt();
    const next = await listMembers(jwt, team.orgId, signal);
    if (!signal?.aborted) setData(next);
  }, [getTeamJwt, team.orgId]);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError(null);
    setSearch('');
    load(controller.signal).catch((cause) => {
      if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Unable to load members.');
    });
    return () => controller.abort();
  }, [load]);

  const run = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key);
    setError(null);
    setMessage(null);
    try {
      await action();
      await load();
      setMessage(success);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Member update failed.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Invites are sent one at a time and their failures collected rather than
   * aborting the batch: pasting ten addresses where one already belongs to the
   * organization should still invite the other nine, and say which one did not
   * go through.
   */
  const sendInvites = async (emails: string[], role: OrgRole) => {
    setBusy('invite');
    setError(null);
    setMessage(null);
    const result: InviteBatchResult = { sent: [], failed: [] };
    try {
      const jwt = await getTeamJwt();
      for (const email of emails) {
        try {
          await inviteMember(jwt, team.orgId, email, role);
          result.sent.push(email);
        } catch (cause) {
          result.failed.push({ email, failure: describeInviteFailure(cause) });
        }
      }
      await load();
      const summary = summarizeInviteBatch(result);
      if (result.sent.length > 0) setMessage(summary);
    } finally {
      setBusy(null);
    }
    // The popover only closes on a clean run; a failed batch keeps the typed
    // addresses on screen so they can be corrected instead of retyped. It
    // reports the failures itself, so the panel banner stays about successes.
    if (result.failed.length > 0) throw new Error(describeInviteFailures(result));
  };

  const partition = useMemo(() => partitionMembership(data?.members ?? []), [data]);
  const visibleActive = useMemo(
    () => sortMembers(searchMembers(partition.active, search), sort),
    [partition.active, search, sort],
  );
  const visiblePending = useMemo(
    () => sortMembers(searchMembers(partition.pending, search), sort),
    [partition.pending, search, sort],
  );

  if (error && !data) {
    return <PanelState tone="error"><strong>Members are unavailable</strong><span>{error}</span></PanelState>;
  }
  if (!data) return <PanelState>Loading organization members…</PanelState>;

  const canAdmin = canManageMembers(data.callerRole);
  const searching = search.trim().length > 0;
  const rowBusy = busy !== null;

  return (
    <div
      className="organization-members flex w-full flex-col gap-4"
      style={{ containerType: 'inline-size', containerName: 'organization-members' }}
    >
      {message && (
        <div
          className="inline-success rounded-[0.6rem] border border-[var(--nim-success)] bg-[color-mix(in_srgb,var(--nim-success)_12%,transparent)] px-4 py-3 text-[0.85rem] text-[var(--nim-success)]"
          role="status"
        >
          {message}
        </div>
      )}
      {error && (
        <div
          className="inline-alert rounded-[0.6rem] border border-[var(--nim-error)] bg-[color-mix(in_srgb,var(--nim-error)_12%,transparent)] px-4 py-3 text-[0.85rem] text-[var(--nim-error)]"
          role="alert"
        >
          {error}
        </div>
      )}

      <section className={`members-card ${CARD}`}>
        <header className="members-toolbar flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <h3 className={SECTION_TITLE}>Roles and members</h3>
            <p className={SECTION_SUBTITLE}>
              {partition.active.length} {partition.active.length === 1 ? 'member' : 'members'}
              {partition.pending.length > 0 && ` · ${partition.pending.length} pending`}
              {' · you are '}{data.callerRole}
            </p>
          </div>
          <CollabSearchInput
            className="w-full basis-full [@container_organization-members_(min-width:44rem)]:w-64 [@container_organization-members_(min-width:44rem)]:basis-auto"
            value={search}
            onChange={setSearch}
            placeholder="Search members..."
            label="Search organization members"
          />
          <MemberSortMenu sort={sort} onChange={setSort} />
          <InviteMembersPopover
            callerRole={data.callerRole}
            // Not disabled by its own in-flight batch: this button is the open
            // popover's anchor, and disabling it mid-submit drops focus out of
            // the form the admin is still looking at.
            disabled={rowBusy && busy !== 'invite'}
            onInvite={sendInvites}
          />
        </header>

        <div className="member-list mt-4 border-t border-nim">
          {visibleActive.length === 0 ? (
            <p className="m-0 py-6 text-center text-[0.82rem] text-nim-muted">
              {searching ? `No members match "${search.trim()}".` : 'This organization has no joined members yet.'}
            </p>
          ) : visibleActive.map((member) => {
            const identity = memberIdentity(member);
            // Role and Remove share one grid cell so the row keeps three tracks
            // when the joined date drops out at narrow widths, rather than
            // wrapping Remove onto a line of its own.
            return (
              <div
                className="member-row grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b border-nim py-2.5
                  [@container_organization-members_(min-width:44rem)]:grid-cols-[auto_minmax(0,1fr)_6.5rem_auto]"
                key={member.memberId}
              >
                <MemberAvatar initials={identity.initials} />
                <div className="member-identity min-w-0">
                  <strong className="block truncate text-[0.86rem] font-semibold text-nim">{identity.title}</strong>
                  {identity.subtitle && (
                    <span className="block truncate text-[0.72rem] text-nim-muted">{identity.subtitle}</span>
                  )}
                </div>
                <span className="member-joined hidden text-[0.72rem] text-nim-muted [@container_organization-members_(min-width:44rem)]:block">
                  {formatDate(member.createdAt)}
                </span>
                <div className="flex items-center justify-end gap-1.5">
                  <RoleControl
                    member={member}
                    callerRole={data.callerRole}
                    disabled={!canAdmin || rowBusy}
                    onChange={(role) => void run(
                      member.memberId,
                      async () => updateMemberRole(await getTeamJwt(), team.orgId, member.memberId, role),
                      `Updated ${identity.title}.`,
                    )}
                  />
                  <button
                    className={`member-remove ${BUTTON_GHOST_DANGER}`}
                    type="button"
                    disabled={!canAdmin || member.memberId === data.callerMemberId || rowBusy}
                    title={member.memberId === data.callerMemberId ? 'You cannot remove yourself' : 'Remove from organization'}
                    onClick={() => void run(
                      `remove:${member.memberId}`,
                      async () => removeMember(await getTeamJwt(), team.orgId, member.memberId),
                      `Removed ${identity.title}.`,
                    )}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {partition.pending.length > 0 && (
        <section className={`pending-invitations-card ${CARD}`}>
          <header>
            <h3 className={SECTION_TITLE}>Pending invitations</h3>
            <p className={SECTION_SUBTITLE}>
              Invited but not yet signed in. Resending mails a fresh link; revoking withdraws the invitation.
            </p>
          </header>
          <div className="pending-invitation-list mt-4 border-t border-nim">
            {visiblePending.length === 0 ? (
              <p className="m-0 py-6 text-center text-[0.82rem] text-nim-muted">
                No pending invitations match &quot;{search.trim()}&quot;.
              </p>
            ) : visiblePending.map((member) => {
              const identity = memberIdentity(member);
              return (
                <div
                  className="pending-invitation-row grid items-center gap-3 border-b border-nim py-2.5
                    grid-cols-[auto_minmax(0,1fr)_auto]
                    [@container_organization-members_(min-width:44rem)]:grid-cols-[auto_minmax(0,1fr)_auto_auto]"
                  key={member.memberId}
                >
                  <MemberAvatar initials={identity.initials} muted />
                  <div className="min-w-0">
                    <strong className="block truncate text-[0.86rem] font-semibold text-nim">{member.email}</strong>
                    <span className="block truncate text-[0.72rem] text-nim-muted">
                      Invited {formatDate(member.createdAt)} as {member.role}
                    </span>
                  </div>
                  <span className={`${PILL} hidden text-[var(--nim-warning)] bg-[color-mix(in_srgb,var(--nim-warning)_14%,transparent)] [@container_organization-members_(min-width:44rem)]:inline-flex`}>
                    {member.status.replaceAll('_', ' ')}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      className={`invitation-resend ${BUTTON_GHOST}`}
                      type="button"
                      disabled={!canAdmin || rowBusy}
                      onClick={() => void run(
                        `resend:${member.memberId}`,
                        async () => resendInvitation(await getTeamJwt(), team.orgId, member.email, member.role),
                        `Invitation resent to ${member.email}.`,
                      )}
                    >
                      {busy === `resend:${member.memberId}` ? 'Sending…' : 'Resend'}
                    </button>
                    <button
                      className={`invitation-revoke ${BUTTON_GHOST_DANGER}`}
                      type="button"
                      disabled={!canAdmin || rowBusy}
                      onClick={() => void run(
                        `revoke:${member.memberId}`,
                        async () => revokeInvitation(await getTeamJwt(), team.orgId, member.memberId),
                        `Invitation to ${member.email} revoked.`,
                      )}
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {canAdmin && (
        <InviteLinksPanel getTeamJwt={getTeamJwt} orgId={team.orgId} callerRole={data.callerRole} disabled={rowBusy} />
      )}

      {canAdmin && team.projects.length > 0 && (
        <ProjectAccessPanel
          getTeamJwt={getTeamJwt}
          team={team}
          members={partition.active}
          disabled={rowBusy}
        />
      )}
    </div>
  );
}
